import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { TrashIcon, PencilIcon, UserIcon, ArrowLeftIcon, CloudIcon } from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { createUserAsync, updateUserAsync, deleteUserAsync } from '../store/usersSlice';
import { User } from '../types';
import Card from '../components/Card';
import Button from '../components/Button';
import Modal from '../components/Modal';
import AzureAdUserSearchModalForUsers from '../components/AzureAdUserSearchModalForUsers';

interface UserFormData {
  name: string;
  email: string;
  role: 'admin' | 'site-admin' | 'key-requester' | 'user';
  locationAccess: string[];
}

export default function Users() {
  const dispatch = useAppDispatch();
  const { users, loading } = useAppSelector((state) => state.users);
  const locations = useAppSelector((state) => state.locations.locations);
  const currentUser = useAppSelector((state) => state.users.currentUser);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAzureAdModalOpen, setIsAzureAdModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { register, handleSubmit, reset, setValue, watch } = useForm<UserFormData>();

  const selectedRole = watch('role');

  const handleOpenModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setValue('name', user.name);
      setValue('email', user.email);
      setValue('role', user.role);
      setValue('locationAccess', user.locationAccess);
    } else {
      setEditingUser(null);
      reset({
        locationAccess: [],
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    reset();
  };

  const onSubmit = async (data: UserFormData) => {
    setIsSaving(true);

    // Check if trying to change last admin's role
    if (editingUser && editingUser.role === 'admin' && data.role !== 'admin') {
      const adminCount = users.filter(u => u.role === 'admin' && u.id !== editingUser.id).length;
      if (adminCount === 0) {
        alert('Cannot change the last admin user\'s role. At least one admin must remain.');
        setIsSaving(false);
        return;
      }
    }

    // Admins and key-requesters have access to all locations
    const locationAccess = data.role === 'admin' || data.role === 'key-requester'
      ? locations.map(loc => loc.id)
      : data.locationAccess;

    try {
      if (editingUser) {
        const updatedUser: User = {
          ...editingUser,
          ...data,
          locationAccess,
        };
        await dispatch(updateUserAsync(updatedUser)).unwrap();
      } else {
        const newUser: User = {
          id: `user-${Date.now()}`,
          ...data,
          locationAccess,
        };
        await dispatch(createUserAsync(newUser)).unwrap();
      }

      handleCloseModal();
    } catch (error) {
      console.error('Failed to save user:', error);
      const errorMessage = (error as Error).message || 'Failed to save. Please try again.';
      alert(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const isLastAdmin = (user: User) => {
    if (user.role !== 'admin') return false;
    const adminCount = users.filter(u => u.role === 'admin').length;
    return adminCount === 1;
  };

  const handleDelete = async (user: User) => {
    if (isLastAdmin(user)) {
      alert('Cannot delete the last admin user. At least one admin must remain.');
      return;
    }

    if (confirm(`Are you sure you want to remove ${user.name}?`)) {
      try {
        await dispatch(deleteUserAsync(user.id)).unwrap();
      } catch (error) {
        console.error('Failed to delete user:', error);
        const errorMessage = (error as Error).message || 'Failed to delete. Please try again.';
        alert(errorMessage);
      }
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300';
      case 'site-admin':
        return 'bg-success/20 dark:bg-green-900/30 text-green-800 dark:text-green-300';
      case 'key-requester':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300';
      default:
        return 'bg-secondary-200 dark:bg-secondary-700 text-secondary-800 dark:text-secondary-300';
    }
  };

  const canManageUsers = currentUser?.role === 'admin';

  // Sort users by role: admin > site-admin > key-requester > user
  const sortedUsers = useMemo(() => {
    const roleOrder: Record<string, number> = {
      'admin': 0,
      'site-admin': 1,
      'key-requester': 2,
      'user': 3,
    };
    return [...users].sort((a, b) => {
      const orderDiff = roleOrder[a.role] - roleOrder[b.role];
      if (orderDiff !== 0) return orderDiff;
      // Secondary sort by name within same role
      return a.name.localeCompare(b.name);
    });
  }, [users]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/settings"
            className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 text-secondary-600 dark:text-secondary-400 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Users</h1>
            <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
              Manage user accounts and permissions
            </p>
          </div>
        </div>
        {canManageUsers && (
          <Button onClick={() => setIsAzureAdModalOpen(true)} disabled={loading}>
            <CloudIcon className="w-5 h-5 mr-2" />
            Add from Azure AD
          </Button>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-secondary-600 dark:text-secondary-400">Loading...</span>
        </div>
      )}

      {/* Users List */}
      <div className="space-y-4">
        {sortedUsers.map((user) => {
          const userLocations = user.role === 'admin' || user.role === 'key-requester'
            ? 'All Locations'
            : locations
                .filter(loc => user.locationAccess.includes(loc.id))
                .map(loc => loc.name)
                .join(', ') || 'No locations';

          return (
            <Card key={user.id}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {user.photoUrl ? (
                    <img
                      src={user.photoUrl}
                      alt={user.name}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="flex-shrink-0 p-2.5 rounded-lg bg-primary-100 dark:bg-primary-900/30">
                      <UserIcon className="w-7 h-7 text-primary-600 dark:text-primary-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">
                        {user.name}
                      </h3>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)}`}>
                        {user.role === 'site-admin' ? 'Site Admin' : user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    </div>
                    <p className="text-sm text-secondary-600 dark:text-secondary-400">
                      {user.email}
                    </p>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                      Access: {userLocations}
                    </p>
                  </div>
                </div>
                {canManageUsers && (
                  <div className="flex gap-2 flex-shrink-0 ml-4">
                    <button
                      onClick={() => handleOpenModal(user)}
                      className="flex items-center px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/50"
                      disabled={loading}
                    >
                      <PencilIcon className="w-4 h-4 mr-2" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(user)}
                      className="flex items-center px-3 py-2 text-sm font-medium text-danger bg-red-50 dark:bg-red-900/30 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={loading || isLastAdmin(user)}
                      title={isLastAdmin(user) ? 'Cannot delete the last admin user' : ''}
                    >
                      <TrashIcon className="w-4 h-4 mr-2" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {users.length === 0 && !loading && (
        <Card>
          <div className="py-12 text-center">
            <UserIcon className="w-12 h-12 mx-auto text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-secondary-100">No users</h3>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              Get started by adding a new user.
            </p>
          </div>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingUser ? 'Edit User' : 'Add User'}
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSubmit(onSubmit)} disabled={isSaving}>
              {isSaving ? 'Saving...' : editingUser ? 'Update' : 'Add'}
            </Button>
          </>
        }
      >
        <form className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              {...register('name', { required: true })}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="John Doe"
              disabled={isSaving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Email *
            </label>
            <input
              type="email"
              {...register('email', { required: true })}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="john@example.com"
              disabled={isSaving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Role *
            </label>
            <select
              {...register('role', { required: true })}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSaving || (editingUser ? isLastAdmin(editingUser) : false)}
            >
              <option value="">Select a role</option>
              <option value="admin">Admin (Full Access)</option>
              <option value="site-admin">Site Admin (Location-specific)</option>
              <option value="key-requester">Key Requester (HR - Request keys)</option>
              <option value="user">User (Read-only)</option>
            </select>
            {editingUser && isLastAdmin(editingUser) && (
              <p className="mt-1 text-sm text-warning">
                Cannot change the last admin user's role. At least one admin must remain.
              </p>
            )}
          </div>
          {(selectedRole === 'site-admin' || selectedRole === 'user') && (
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Location Access *
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto border border-secondary-300 dark:border-secondary-600 rounded-lg p-3 bg-white dark:bg-secondary-700">
                {locations.map((location) => (
                  <label key={location.id} className="flex items-center">
                    <input
                      type="checkbox"
                      value={location.id}
                      {...register('locationAccess')}
                      className="w-4 h-4 text-primary-600 border-secondary-300 dark:border-secondary-600 rounded focus:ring-primary-500"
                      disabled={isSaving}
                    />
                    <span className="ml-2 text-sm text-secondary-900 dark:text-secondary-100">
                      {location.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {selectedRole === 'admin' && (
            <div className="p-3 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
              <p className="text-sm text-primary-800 dark:text-primary-200">
                Admins have access to all locations and full system permissions.
              </p>
            </div>
          )}
        </form>
      </Modal>

      {/* Azure AD User Search Modal */}
      <AzureAdUserSearchModalForUsers
        isOpen={isAzureAdModalOpen}
        onClose={() => setIsAzureAdModalOpen(false)}
      />
    </div>
  );
}
